import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { DisplayPackage } from '../types/revenuecat';
import { createStripeSubscription, getOrCreateStripeCustomer } from '../services/stripePricing';
import { durationToDays } from '../utils/duration';
import { getPaymentErrorMessage } from '../utils/firebaseErrors';
import { trackBillingSubmitted, trackBillingSuccess, trackBillingFailed } from '../services/analytics';
import { clearSubscriptionCache } from '../utils/subscriptionCache';
import { clearEarlyBirdOffer } from '../utils/earlyBird';
import { formatLongDate } from '../utils/formatDate';
import { cancelStripeSubscription } from '../services/stripe';
import { t } from '../i18n';

interface StripePaymentFormProps {
  selectedPackage: DisplayPackage;
  userEmail: string;
  userId: string;
  couponCode?: string;
  isPromotionalUpgrade?: boolean;
  promotionalExpiryDate?: string | null;
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#1B2B48',
      fontFamily: 'Figtree, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontSize: '16px',
      '::placeholder': {
        color: 'rgba(27, 43, 72, 0.5)',
      },
    },
    invalid: {
      color: '#FF4444',
      iconColor: '#FF4444',
    },
  },
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  selectedPackage,
  userEmail,
  userId,
  couponCode,
  isPromotionalUpgrade,
  promotionalExpiryDate
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [email, setEmail] = useState(userEmail);
  // CAD prices are the McDonald's Canada early bird offer.
  const [country, setCountry] = useState(selectedPackage.product.currency === 'cad' ? 'Canada' : 'United Kingdom');
  const [postalCode, setPostalCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError(t('billing.stripeNotLoaded'));
      return;
    }

    setLoading(true);
    setError('');

    // Track billing submission
    const planName = selectedPackage.product.title || selectedPackage.identifier;
    const amount = selectedPackage.product.price || 0;
    const currency = selectedPackage.product.currency || 'gbp';

    trackBillingSubmitted(
      planName,
      amount,
      currency,
      email,
      userId
    );

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);

      if (!cardNumberElement) {
        throw new Error('Card element not found');
      }

      // Create payment method
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          email,
          address: {
            postal_code: postalCode,
            country: getCountryCode(country),
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      console.log('Payment method created:', paymentMethod);

      // Step 1: Get or create Stripe customer
      const customerResult = await getOrCreateStripeCustomer(email, userId);

      if (!customerResult.success || !customerResult.customerId) {
        throw new Error(customerResult.error || 'Failed to create customer');
      }

      console.log('Customer ID:', customerResult.customerId);

      // Step 2: Create subscription with the payment method
      // Convert trial_duration from ISO 8601 format (e.g., "P7D") to days (e.g., 7)
      let trialDays = durationToDays(selectedPackage.product.trial_duration);

      console.log('Initial trial days from plan:', trialDays);
      console.log('Promotional upgrade props:', { isPromotionalUpgrade, promotionalExpiryDate });

      // If upgrading from promotional subscription, calculate trial period until promo ends
      if (isPromotionalUpgrade && promotionalExpiryDate) {
        const now = new Date();
        const expiryDate = new Date(promotionalExpiryDate);
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        console.log('Promo calculation:', {
          now,
          expiryDate,
          millisecondsDiff: expiryDate.getTime() - now.getTime(),
          daysUntilExpiry
        });

        // Use the longer trial period (existing trial or promotional period remaining)
        trialDays = Math.max(trialDays || 0, Math.max(0, daysUntilExpiry));

        console.log('Promotional upgrade detected:', {
          promotionalExpiryDate,
          daysUntilExpiry,
          calculatedTrialDays: trialDays
        });
      }

      console.log('⭐ SENDING TO STRIPE BACKEND:', {
        customerId: customerResult.customerId,
        priceId: selectedPackage.product.price_id,
        trialDays: trialDays,
        isPromotionalUpgrade: isPromotionalUpgrade,
        promotionalExpiryDate: promotionalExpiryDate
      });

      const subscriptionResult = await createStripeSubscription(
        customerResult.customerId,
        selectedPackage.product.price_id, // Use price_id, not store_identifier
        paymentMethod.id,
        couponCode,
        trialDays
      );

      if (!subscriptionResult.success) {
        throw new Error(subscriptionResult.error || 'Failed to create subscription');
      }

      console.log('Subscription created:', subscriptionResult.subscriptionId);
      console.log('Subscription status:', subscriptionResult.status);

      // Step 3: Handle 3D Secure authentication if required
      if (subscriptionResult.clientSecret && subscriptionResult.status === 'incomplete') {
        console.log('3D Secure authentication required');

        // Confirm the payment with 3D Secure
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          subscriptionResult.clientSecret,
          {
            payment_method: paymentMethod.id,
            return_url: `${window.location.origin}/subscription`, // Return to subscription page after auth
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }

        console.log('Payment intent confirmed:', paymentIntent?.status);

        // Check if payment was successful
        if (paymentIntent?.status !== 'succeeded') {
          throw new Error(t('billing.paymentAuthFailed'));
        }
      }

      // Trials take no payment today, so authenticate the card now (3D Secure
      // if the bank asks) for the charge when the trial ends. If the card
      // fails, cancel the new subscription so the runner can retry with
      // another card rather than being charged on a card that won't work.
      if (subscriptionResult.setupClientSecret) {
        const { error: setupError } = await stripe.confirmCardSetup(
          subscriptionResult.setupClientSecret,
          { payment_method: paymentMethod.id }
        );
        if (setupError) {
          // cancelStripeSubscription reports failure in its result, it doesn't throw.
          const cancelled = await cancelStripeSubscription(userId, subscriptionResult.subscriptionId, false);
          if (!cancelled?.success) {
            console.error('Could not cancel unverified subscription:', subscriptionResult.subscriptionId, cancelled?.message);
            throw new Error(t('billing.cardVerificationCancelFailed'));
          }
          throw new Error(setupError.message || t('billing.paymentAuthFailed'));
        }
      }

      // Track successful billing
      trackBillingSuccess(
        planName,
        amount,
        currency,
        email,
        userId,
        subscriptionResult.subscriptionId
      );

      // Clear the subscription cache so it refreshes with the new subscription
      clearSubscriptionCache(userId);
      clearEarlyBirdOffer();

      // Wait a moment for RevenueCat to sync the subscription (usually takes 1-2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Navigate to success page with refresh flag to skip cache
      alert(t('billing.subscriptionStarted'));
      navigate('/subscription?refresh=true'); // Force refresh to get new subscription data

    } catch (err: any) {
      console.error('Payment error:', err);
      const errorMessage = getPaymentErrorMessage(err);
      setError(errorMessage);

      // Track failed billing
      trackBillingFailed(
        planName,
        amount,
        currency,
        errorMessage,
        email,
        userId
      );
    } finally {
      setLoading(false);
    }
  };

  const getCountryCode = (countryName: string): string => {
    const countryMap: Record<string, string> = {
      'United Kingdom': 'GB',
      'United States': 'US',
      'Canada': 'CA',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Spain': 'ES',
      'Italy': 'IT',
    };
    return countryMap[countryName] || 'GB';
  };

  return (
    <form onSubmit={handleSubmit} className="billing-form">
      {error && (
        <div className="error-message" style={{
          color: '#FF9999',
          marginBottom: '20px',
          padding: '12px',
          background: 'rgba(255, 153, 153, 0.1)',
          borderRadius: '8px'
        }}>
          {error}
        </div>
      )}

      {isPromotionalUpgrade && promotionalExpiryDate && (
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          background: 'rgba(228, 255, 120, 0.2)',
          borderRadius: '8px',
          border: '1px solid #E4FF78',
          color: '#1B2B48'
        }}>
          <strong>{t('billing.deferredBillingLabel')}</strong> {t('billing.deferredBillingMessage', { date: formatLongDate(promotionalExpiryDate) })}
        </div>
      )}

      <div className="form-field">
        <label htmlFor="email">{t('billing.emailLabel')}</label>
        <input
          type="email"
          id="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('billing.emailLabel')}
          required
          disabled={loading}
        />
      </div>

      <div className="form-field">
        <label htmlFor="cardNumber">{t('billing.cardNumberLabel')}</label>
        <div className="stripe-element-wrapper">
          <CardNumberElement
            id="cardNumber"
            options={CARD_ELEMENT_OPTIONS}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-field">
          <label htmlFor="cardExpiry">{t('billing.expirationDateLabel')}</label>
          <div className="stripe-element-wrapper">
            <CardExpiryElement
              id="cardExpiry"
              options={CARD_ELEMENT_OPTIONS}
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="cardCvc">{t('billing.securityCodeLabel')}</label>
          <div className="stripe-element-wrapper">
            <CardCvcElement
              id="cardCvc"
              options={CARD_ELEMENT_OPTIONS}
            />
          </div>
        </div>
      </div>

      <div className="form-field">
        <label htmlFor="country">{t('billing.countryLabel')}</label>
        <select
          id="country"
          name="country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          required
          disabled={loading}
        >
          <option value="United Kingdom">United Kingdom</option>
          <option value="United States">United States</option>
          <option value="Canada">Canada</option>
          <option value="Australia">Australia</option>
          <option value="Germany">Germany</option>
          <option value="France">France</option>
          <option value="Spain">Spain</option>
          <option value="Italy">Italy</option>
        </select>
      </div>

      <div className="form-field">
        <label htmlFor="postalCode">{t('billing.postalCodeLabel')}</label>
        <input
          type="text"
          id="postalCode"
          name="postalCode"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value)}
          placeholder={t('billing.postalCodeLabel')}
          required
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="btn-start-trial"
      >
        {loading ? t('billing.processing') : t('billing.startTrial')}
      </button>

      <div className="security-notice">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px' }}>
          <path d="M8 1L3 3V7C3 10.5 5.5 13.5 8 15C10.5 13.5 13 10.5 13 7V3L8 1Z"
                stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <path d="M6.5 8L7.5 9L9.5 7" stroke="#4CAF50" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>{t('billing.securityNotice')}</span>
      </div>

      <div className="disclaimer">
        <p>{t('billing.termsOfService')}</p>
        <p>{t('billing.autoRenew')}</p>
        <p>{t('billing.cancelAnytime')}</p>
      </div>
    </form>
  );
};

export default StripePaymentForm;
